import { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Alert,
  Loader,
  Title,
  Divider,
  Card,
  ActionIcon,
  Modal,
  NumberInput,
  TextInput,
  Textarea,
  Select
} from '@mantine/core';
import {
  IconRefresh,
  IconCheck,
  IconX,
  IconEdit,
  IconClock,
  IconAlertCircle
} from '@tabler/icons-react';
import { api as defaultApi } from '../../services/api';

const SpecialRequestsList = ({ isPrincess = false, api = defaultApi }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [counterOfferModal, setCounterOfferModal] = useState(false);
  const [counterOfferData, setCounterOfferData] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getSpecialRequests();
      setRequests(response.requests || []);
    } catch (err) {
      console.error('Error fetching special requests:', err);
      setError(err.message || 'Failed to load special requests');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleAccept = async (requestId) => {
    try {
      await api.acceptSpecialRequest(requestId);
      fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to accept request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await api.rejectSpecialRequest(requestId);
      fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    }
  };

  const handleCounterOffer = async () => {
    try {
      await api.counterOfferSpecialRequest(selectedRequest.id, counterOfferData);
      setCounterOfferModal(false);
      setSelectedRequest(null);
      setCounterOfferData({});
      fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to submit counter offer');
    }
  };

  const handleAcceptCounterOffer = async (requestId) => {
    try {
      await api.acceptCounterOffer(requestId);
      fetchRequests();
    } catch (err) {
      setError(err.message || 'Failed to accept counter offer');
    }
  };

  const openCounterOfferModal = (request) => {
    setSelectedRequest(request);
    setCounterOfferData({
      counter_nail_polish_color: request.nail_polish_color,
      counter_design: request.design,
      counter_service_type: request.service_type,
      counter_special_notes: request.special_notes,
      counter_number_of_pictures: request.number_of_pictures,
      counter_offer_price: request.offer_price
    });
    setCounterOfferModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'yellow',
      accepted: 'green',
      counter_offered: 'blue',
      rejected: 'red',
      completed: 'gray',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Special Requests</Title>
        <ActionIcon onClick={fetchRequests} variant="light">
          <IconRefresh size="1.2rem" />
        </ActionIcon>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="red">
          {error}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed" ta="center">
            No special requests yet
          </Text>
        </Paper>
      ) : (
        <Stack>
          {requests.map((request) => (
            <Card key={request.id} shadow="sm" p="lg" radius="md" withBorder>
              <Stack>
                <Group justify="space-between">
                  <Group>
                    <Text fw={500}>
                      {isPrincess ? `From: ${request.requester_username}` : `To: ${request.princess_username}`}
                    </Text>
                    <Badge color={getStatusColor(request.status)}>
                      {request.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {formatDate(request.created_at)}
                  </Text>
                </Group>

                <Divider />

                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">Service Type</Text>
                    <Text>{request.service_type}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Polish Color</Text>
                    <Text>{request.nail_polish_color || 'Not specified'}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text>{request.number_of_pictures}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Offer Price</Text>
                    <Text fw={600} size="lg">${request.offer_price}</Text>
                  </div>
                </Group>

                {request.design && (
                  <div>
                    <Text size="sm" c="dimmed">Design</Text>
                    <Text>{request.design}</Text>
                  </div>
                )}

                {request.special_notes && (
                  <div>
                    <Text size="sm" c="dimmed">Special Notes</Text>
                    <Text>{request.special_notes}</Text>
                  </div>
                )}

                {request.status === 'counter_offered' && (
                  <Paper p="md" withBorder bg="blue.0">
                    <Text fw={500} mb="sm">Counter Offer</Text>
                    <Group grow>
                      {request.counter_offer_price && (
                        <div>
                          <Text size="sm" c="dimmed">New Price</Text>
                          <Text fw={600}>${request.counter_offer_price}</Text>
                        </div>
                      )}
                      {request.counter_number_of_pictures && (
                        <div>
                          <Text size="sm" c="dimmed">Pictures</Text>
                          <Text>{request.counter_number_of_pictures}</Text>
                        </div>
                      )}
                    </Group>
                    {request.counter_special_notes && (
                      <div>
                        <Text size="sm" c="dimmed" mt="sm">Counter Notes</Text>
                        <Text>{request.counter_special_notes}</Text>
                      </div>
                    )}
                  </Paper>
                )}

                {isPrincess && request.status === 'pending' && (
                  <Group>
                    <Button
                      leftSection={<IconCheck size="1rem" />}
                      color="green"
                      onClick={() => handleAccept(request.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      leftSection={<IconEdit size="1rem" />}
                      variant="light"
                      onClick={() => openCounterOfferModal(request)}
                    >
                      Counter Offer
                    </Button>
                    <Button
                      leftSection={<IconX size="1rem" />}
                      color="red"
                      variant="light"
                      onClick={() => handleReject(request.id)}
                    >
                      Reject
                    </Button>
                  </Group>
                )}

                {!isPrincess && request.status === 'counter_offered' && (
                  <Group>
                    <Button
                      leftSection={<IconCheck size="1rem" />}
                      color="green"
                      onClick={() => handleAcceptCounterOffer(request.id)}
                    >
                      Accept Counter Offer
                    </Button>
                    <Button
                      leftSection={<IconX size="1rem" />}
                      color="red"
                      variant="light"
                      onClick={() => handleReject(request.id)}
                    >
                      Reject
                    </Button>
                  </Group>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Modal
        opened={counterOfferModal}
        onClose={() => setCounterOfferModal(false)}
        title="Make Counter Offer"
        size="lg"
      >
        <Stack>
          <TextInput
            label="Nail Polish Color"
            value={counterOfferData.counter_nail_polish_color || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              counter_nail_polish_color: e.target.value
            })}
          />

          <Textarea
            label="Design"
            value={counterOfferData.counter_design || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              counter_design: e.target.value
            })}
          />

          <Select
            label="Service Type"
            data={[
              { value: 'paraffin', label: 'Paraffin' },
              { value: 'french', label: 'French' },
              { value: 'gel', label: 'Gel' },
              { value: 'athletic', label: 'Athletic' }
            ]}
            value={counterOfferData.counter_service_type || ''}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              counter_service_type: value
            })}
          />

          <Textarea
            label="Special Notes"
            value={counterOfferData.counter_special_notes || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              counter_special_notes: e.target.value
            })}
          />

          <NumberInput
            label="Number of Pictures"
            min={1}
            max={20}
            value={counterOfferData.counter_number_of_pictures || 5}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              counter_number_of_pictures: value
            })}
          />

          <NumberInput
            label="Counter Offer Price ($)"
            min={10}
            max={500}
            precision={2}
            value={counterOfferData.counter_offer_price || 50}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              counter_offer_price: value
            })}
          />

          <Group justify="flex-end">
            <Button variant="light" onClick={() => setCounterOfferModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCounterOffer}>
              Submit Counter Offer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default SpecialRequestsList;